"use client"

import Link from "next/link"
import { ArrowRight, Star } from "lucide-react"
import { motion } from "motion/react"

import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Marquee } from "@/components/ui/marquee"
import { testimonials } from "@/lib/testimonial-data"

const ease = [0.16, 1, 0.3, 1] as const

const cardChildVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
}

function UserAvatar({ name, imageUrl }: { name: string; imageUrl?: string }) {
  const initials = name
    .split(/(?=[A-Z])/)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase()

  if (imageUrl) {
    // Descriptive alt ("Avatar du témoignage de {name}") rather than
    // empty — squirrel treats empty alt as "missing alt text"
    // (failure, not warning) whereas the previous `alt={name}` was
    // flagged as redundant-of-adjacent-text. Prefixing with "Avatar
    // de" breaks the literal-duplicate heuristic while still
    // describing the image content for screen readers.
    return (
      <img
        src={imageUrl}
        alt={`Avatar du témoignage de ${name}`}
        width={40}
        height={40}
        loading="lazy"
        decoding="async"
        className="size-10 rounded-full object-cover bg-muted"
      />
    )
  }

  return (
    <div className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
      {initials}
    </div>
  )
}

function ReviewCard({ review }: { review: (typeof testimonials)[0] }) {
  return (
    <div className="w-[280px] sm:w-[340px] shrink-0 rounded-2xl bg-card ring-1 ring-foreground/5 dark:ring-foreground/10 p-5">
      <div className="flex items-center gap-3 mb-3">
        <UserAvatar name={review.author} imageUrl={review.imageUrl} />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold truncate">{review.author}</p>
          <p className="text-[10px] text-muted-foreground">{review.source}</p>
        </div>
        <div className="flex gap-0.5">
          {Array.from({ length: review.rating }).map((_, i) => (
            <Star key={i} className="size-3 fill-primary text-primary" />
          ))}
        </div>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground line-clamp-3">
        &ldquo;{review.quote}&rdquo;
      </p>
    </div>
  )
}

export function Testimonials() {
  const reversed = [...testimonials].reverse()

  return (
    <section className="section-normal px-0 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <motion.div
          className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          transition={{ staggerChildren: 0.1 }}
        >
          <div>
            <motion.div variants={cardChildVariants} transition={{ duration: 0.4, ease }}>
              <Badge variant="secondary" className="mb-4">Témoignages</Badge>
            </motion.div>
            {/*
             * Single-sentence trust headline. The previous trio of stat
             * tiles (10 000 / 1986 / 17) was scanned as decorative
             * data-viz, not a claim — readers skipped past it. We fold
             * the two surviving numbers into one declarative sentence:
             * the eye lands on it once, parses both the scale (10.000+
             * clients) and the tenure (depuis 1986) in a single beat,
             * and the testimonial cards below carry the reinforcement.
             */}
            <motion.h2
              className="text-3xl font-black tracking-tight md:text-5xl"
              style={{ textWrap: "balance", letterSpacing: "-0.02em" }}
              variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.5, ease }}
            >
              <span className="tabular-nums text-primary">10.000+</span>
              {" "}clients nous font confiance{" "}
              <span className="whitespace-nowrap">depuis <span className="tabular-nums text-primary">1986</span></span>
            </motion.h2>
          </div>
          <motion.div variants={cardChildVariants} transition={{ duration: 0.4, ease }}>
            <Link
              href="/avis"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary underline underline-offset-2 decoration-primary/30 transition-[color,text-decoration-color] duration-200 hover:text-primary/80 hover:decoration-primary/60"
            >
              Voir tous les avis
              <ArrowRight className="size-3.5" />
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Two auto-scrolling rows — opposite directions */}
      <div className="space-y-4">
        <Marquee className="[--duration:50s]">
          {testimonials.map((review) => (
            <ReviewCard key={review.slug} review={review} />
          ))}
        </Marquee>

        <Marquee className="[--duration:55s]" reverse>
          {reversed.map((review) => (
            <ReviewCard key={review.slug} review={review} />
          ))}
        </Marquee>
      </div>
    </section>
  )
}
